<template>
  <div class="player-box">
    <img class="episode-avatar" :src="constants.phImg600x600" v-if="initLoading || !episode || (!episode.image && (!episode.channel || !episode.channel.image))">
    <img-with-placeholder class="episode-avatar" :src="constants.phImg600x600" :data-src="sizeImg(episode.image_base_url, 600, 600)" :alt="episode.image.alt_text" v-else-if="episode.image" />
    <img-with-placeholder class="episode-avatar" :src="constants.phImg600x600" :data-src="sizeImg(episode.channel.image_base_url, 600, 600)" :alt="episode.channel.image.alt_text" v-else />
    <div class="controls">
      <button class="fast-backward flat-button"
        :disabled="episodeId !== player.episodeId"
        v-bind:class="{ 'ph-icon': initLoading, disabled: episodeId !== player.episodeId }"
        @click="rewind()"
      >
        <icon-fast-backward />
        <span class="dur">{{ currentUser.rewind_duration }}</span>
      </button>
      <button class="play flat-button js-button-toggle"
        v-bind:class="{ 'ph-icon': initLoading, switched: !initLoading && episodeId === player.episodeId && player.playing, buffering: !initLoading && episodeId === player.episodeId && player.buffering }"
        @click="handlePlayOrPause()"
      >
        <icon-play/>
      </button>
      <button class="fast-forward flat-button"
        :disabled="episodeId !== player.episodeId"
        v-bind:class="{ 'ph-icon': initLoading, disabled: episodeId !== player.episodeId }"
        @click="skip()"
      >
        <icon-fast-forward />
        <span class="dur">{{ currentUser.skip_duration }}</span>
      </button>
    </div>
    <div class="range-line">
      <div class="time-line small">
        <span class="time" v-if="initLoading">-</span>
        <span class="time" v-else-if="episodeId === player.episodeId">{{ player.currentTime | secondsToDuration }}</span>
        <span class="time" v-else>-</span>
        /
        <span class="duration" v-if="initLoading">-</span>
        <span class="duration" v-else-if="episodeId === player.episodeId">{{ (player.duration === 0 ? episode.duration : player.duration) | secondsToDuration }}</span>
        <span class="duration" v-else-if="episode && episode.duration">{{ episode.duration | secondsToDuration }}</span>
        <span class="duration" v-else>-</span>
      </div>
      <v-ons-range style="width: 100%;" modifier="sm" value="0" disabled
        v-show="initLoading || episodeId !== player.episodeId"
      ></v-ons-range>
      <v-ons-range style="width: 100%;" modifier="sm"
        v-model="playerCurrentPerc"
        :disabled="initLoading || episodeId !== player.episodeId"
        v-show="!initLoading && episodeId === player.episodeId"
      ></v-ons-range>
    </div>
    <ul class="tools no-bullet">
      <li>
        <button class="download flat-button"
          v-bind:class="{ 'ph-icon': initLoading, pending: episode && episode._download_status === 'PENDING', switched: episode && episode._download_status === 'DOWNLOADING', complete: episode && episode._download_status === 'DOWNLOADED' }"
          @click="downloadOrCancel(episode, episode && episode.channel)"
        >
          <icon-download v-if="initLoading" />
          <icon-download v-else :episode="episode" />
          <span class="txt" v-if="episode && episode._download_status === 'DOWNLOADING'">{{ episode._download_progress === 1 ? $t("episode.download.processing") : '' + Math.round(episode._download_progress*100) + '%' }}</span>
          <span class="txt" v-else-if="episode && episode._download_status === 'PENDING'">{{ $t("episode.download.pending") }}</span>
          <span class="txt" v-else-if="episode && episode._download_status === 'DOWNLOADED'">{{ $t("episode.download.downloaded") }}</span>
          <span class="txt" v-else>{{ $t("episode.download.default") }}</span>
        </button>
      </li>
      <li>
        <button class="like js-button-toggle flat-button"
          v-bind:class="{ switched: episode && episode.is_liked, 'ph-icon': initLoading }"
          :disabled="likeLoading"
          @click="toggleLike()"
        >
          <icon-like />
          <span class="txt">{{ (episode && episode.is_liked) ? $t("episode.dislike") : $t("episode.like") }}</span>
        </button>
      </li>
      <li v-if="false">
        <button class="donate js-donate flat-button"
          v-bind:class="{ 'ph-icon': initLoading }"
          @click="openDonateActionSheet()"
        >
          <icon-donate />
          <span class="txt">Donate</span>
        </button>
      </li>
      <li>
        <button class="timer js-timer flat-button"
          v-bind:class="{ 'ph-icon': initLoading }"
          @click="openSleepTimerActionSheet()"
        >
          <icon-timer />
          <span class="txt">{{ $t("ui.sleep_timer.btn_label") }}</span>
        </button>
      </li>
      <li v-if="googleCastAllowed">
        <button class="cast flat-button" data-host=""
          :disabled="initLoading || googleCastLoading"
          v-bind:class="{ 'ph-icon': initLoading, 'cast-active': player.currentPlayerName === 'GoogleCastPlayer' }"
          @click="toggleGoogleCastWrapper()"
        >
          <icon-cast-android />
          <span class="txt">{{ $t("ui.cast.google_cast_btn_label") }}</span>
        </button>
      </li>
      <li v-if="airPlayAllowed">
        <button class="cast flat-button" data-host=""
          :disabled="initLoading"
          v-bind:class="{ 'ph-icon': initLoading }"
          @click="toggleAirPlay()"
        >
          <icon-cast-ios />
          <span class="txt">{{ $t("ui.cast.air_play_btn_label") }}</span>
        </button>
      </li>
      <li>
        <button class="share flat-button"
          v-bind:class="{ 'ph-icon': initLoading }"
          @click="share()"
        >
          <icon-share-ios class="ios-only" />
          <icon-share-android class="non-ios-only" />
          <span class="txt">{{ $t("episode.share") }}</span>
        </button>
      </li>
      <li>
        <button class="embed js-embed flat-button"
                data-host=""
                @click="copyEmbedCode()"
        >
          <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.28 4.073L2 8.68l4.28 4.608m11.44-9.215L22 8.68l-4.28 4.608M14.649 1L9.68 16.745" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>
          <span class="txt">{{ $t("episode.embed") }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import PlayerHelper from '@/mixins/PlayerHelper';
import DownloadHelper from '@/mixins/DownloadHelper';
import SleepTimerHelper from '@/mixins/SleepTimerHelper';
import moment from 'moment';

export default {
  props: {
    initLoading: Boolean,
    episodeId: Number,
    episode: Object,
  },
  mixins: [PlayerHelper, SleepTimerHelper, DownloadHelper],

  data() {
    return {
      likeLoading: false,
      googleCastLoading: false,
      googleCastAllowed: (!!window.cordova || this.$ons.platform.isChrome()) && (!!window.cast || !!window.chrome.cast),
      airPlayAllowed: (!!window.cordova && (this.$ons.platform.isIOS() || this.$ons.platform.isIPadOS())) || this.$ons.platform.isSafari(),
    };
  },

  computed: {
    player() {
      return this.$store.state.player;
    },
    playerCurrentPerc: {
      get () { return this.$store.state.player.currentPerc; },
      set (v) { this.$store.dispatch('player/seek', v/100) }
    }
  },

  methods: {
    toggleLike() {
      if (!this.checkAndShowRequireLoginToast()) { return; }
      if (this.showGoOnlineToast(this.$t("messages.go_online_to_like"))) { return; }
      let that = this;
      this.loading = true;
      this.likeLoading = true;
      this.episode.toggleLike(this.currentUser.id)
        .catch(function (error) {
          console.log(error);
          that.showSomethingWrongToast();
        }).finally(() => {
          this.loading = false;
          this.likeLoading = false;
        });
    },
    share() {
      let path = this.$router.resolve({
        name: 'episode',
        params: { id: this.episode.id, slug: this.slugify(this.episode.title, {strict:true}) },
      });
      let urlPrefix = window.location.href;
      urlPrefix = urlPrefix.split('/');
      urlPrefix = urlPrefix[0] + '//' + urlPrefix[2];
      let pathHref = path.href;
      if (this.$isCordova) {
        urlPrefix = 'https://player.edifi.app';
        pathHref = path.href.substr(1);
      }
      let url = urlPrefix + pathHref;
      if (this.$isCordova) {
        window.plugins.socialsharing.shareWithOptions({
          subject: this.$t("channel.subject_for_sharing", { episode_title: this.episode.title, channel_title: this.episode.channel ? this.episode.channel.title : '' }),
          url: url,
        });
      } else {
        if (window.navigator.share) {
          window.navigator.share({
            title: this.$t("channel.subject_for_sharing", { episode_title: this.episode.title, channel_title: this.episode.channel ? this.episode.channel.title : '' }),
            url: url,
          });
        } else {
          this.copyToClipboard(url);
          this.$ons.notification.toast(this.$t("episode.link_copied"), { timeout: 3000, animation: 'fall' });
        }
      }
    },
    copyLink() {
      let urlPrefix = "https://player.edifi.app/episodes/";
      let pathHref = this.slugify(this.episode.title, {strict:true}) + '/' + this.episode.id;
      let url = urlPrefix + pathHref;
      this.copyToClipboard(url);
      this.$ons.notification.toast(this.$t("episode.link_copied"), { timeout: 3000, animation: 'fall' });
    },
    copyEmbedCode() {
      let urlPrefix = "https://player.edifi.app/embed/index.html#/episodes/";
      let pathHref = this.slugify(this.episode.title,{strict:true}) + '/' + this.episode.id;
      let url = urlPrefix + pathHref;
      let duration = this.player.duration === 0 ? this.episode.duration : this.player.duration
      if(duration){
        duration = moment.duration(duration, 'seconds').format('h:mm:ss', {
          stopTrim: "mm"
        });
      }else{
        duration = '-:--';
      }

      let embedCode = `<iframe data-duration="${duration}" style="width: 1px; min-width: 100%;" height="510" src="${encodeURI(url)}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe><script src="https:\/\/player.edifi.app/embed/host.js"><\/script>`;
      this.copyToClipboard(embedCode);
      this.$ons.notification.toast(this.$t("episode.embed_copied"), { id:'share-copied', timeout: 3000, animation: 'fall' });
    },
    handlePlayOrPause() {
      this.playOrPause(this.episode, this.episode.channel);
    },
    toggleGoogleCastWrapper() {
      if (this.showGoOnlineToast(this.$t("messages.go_online_to_cast"))) { return; }
      let that = this;
      this.googleCastLoading = true;
      this.toggleGoogleCast(this.episode, this.episode.channel).finally(() => {
        that.googleCastLoading = false;
      });
    },
    toggleAirPlay() {
      if (this.showGoOnlineToast(this.$t("messages.go_online_to_cast"))) { return; }
      this.$ons.notification.alert(this.$t("messages.airplay_coming_soon"), { title: '' });
    },
  },

  mounted() {
    // update cast feature availability
    let updateCastFeatureAvail = () => {
      this.googleCastAllowed = (!!window.cordova || this.$ons.platform.isChrome()) && (!!window.cast || !!window.chrome.cast);
      this.airPlayAllowed = (!!window.cordova && (this.$ons.platform.isIOS() || this.$ons.platform.isIPadOS())) || this.$ons.platform.isSafari();
    };
    window.setTimeout(updateCastFeatureAvail, 1000);
    updateCastFeatureAvail();
  },
};
</script>
